import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, X } from 'lucide-react'
import { approveValuation, listValuationApprovals, rejectValuation } from '../../api/inventory'
import { getConfig, saveConfig } from '../../api/settings'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { useToast } from '../../components/Toast.jsx'
import { fmtDay } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { money, num } from '../sales/salesShared'
import InventoryTabs from './InventoryTabs'
import { qty } from './inventoryShared'

// What the stored memo means, in plain words.
const describe = (memo) => {
  const m = String(memo || '').toLowerCase()
  if (m.includes('(damage)')) return 'Damaged'
  if (m.includes('(theft)')) return 'Lost or stolen'
  if (m.includes('overage')) return 'Found extra'
  if (m.includes('revers')) return 'Worth more again'
  if (m.includes('write-down') || m.includes('nrv')) return 'Worth less now'
  return memo || 'Stock adjustment'
}

// Admins approve stock adjustments over the limit (or ones sent for
// approval) before the stock and the books change.
export default function ValuationApprovals() {
  const { notify } = useToast()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(null)
  const [error, setError] = useState('')
  const [limit, setLimit] = useState('')
  const [savedLimit, setSavedLimit] = useState(0)
  const [savingLimit, setSavingLimit] = useState(false)

  const refresh = async () => {
    try { setRows(await listValuationApprovals() || []) } catch (e) { setError(e.message || 'Could not load approvals.') }
    setLoading(false)
  }
  useEffect(() => {
    refresh()
    getConfig().then((c) => {
      const v = num(c?.inventory_writedown_auto_approve_threshold)
      setSavedLimit(v)
      setLimit(v > 0 ? v.toFixed(2) : '')
    }).catch(() => {})
  }, [])

  const decide = async (r, approve) => {
    const label = `${describe(r.memo).toLowerCase()} adjustment`
    let reason = ''
    if (!approve) {
      reason = window.prompt(`Why are you rejecting this ${label}? (optional)`, '')
      if (reason === null) return
    }
    setBusy(r.je_id)
    setError('')
    try {
      if (approve) await approveValuation(r.je_id)
      else await rejectValuation(r.je_id, reason)
      notify(approve ? 'Approved: the stock and the books are updated' : 'Rejected: nothing changes')
      await refresh()
    } catch (e) {
      setError(e.message || 'Could not save that.')
    } finally {
      setBusy(null)
    }
  }

  const saveLimit = async () => {
    setError('')
    const v = String(limit).trim() === '' ? 0 : Number(limit)
    if (!Number.isFinite(v) || v < 0) { setError('Enter an amount, or leave it blank so nothing needs approval.'); return }
    setSavingLimit(true)
    try {
      await saveConfig({ inventory_writedown_auto_approve_threshold: v })
      setSavedLimit(v)
      notify(v > 0 ? `Adjustments over ${money(v)} now need approval` : 'Adjustments no longer need approval')
    } catch (e) {
      setError(e.message || 'Could not save the limit.')
    } finally {
      setSavingLimit(false)
    }
  }

  return (
    <div className="ci-page">
      <InventoryTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Approvals</h1>
            <div className="ci-title-meta">Stock adjustments waiting for an admin. Nothing changes until you approve.</div>
          </div>
        </div>
      </div>

      {error && <ErrorBanner message={error} />}

      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table">
            <thead>
              <tr>
                <th>Asked</th>
                <th>What happened</th>
                <th>Products</th>
                <th className="num">Amount</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5"><Spinner label="Loading approvals…" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan="5"><div className="ci-empty">Nothing is waiting for approval.</div></td></tr>
              ) : rows.map((r) => (
                <tr key={r.je_id}>
                  <td className="ci-muted-cell">{fmtDay(r.entry_date) || '–'}<span className="cell-sub">{docNo('je', r.je_no)}</span></td>
                  <td className="ci-strong">{describe(r.memo)}</td>
                  <td>
                    {(r.products || []).length ? r.products.map((p) => (
                      <div key={p.product_id}>
                        <Link to={`/inventory/stock/${p.product_id}`}>{p.name || p.sku}</Link>
                        {num(p.qty) > 0 && <span className="sum-muted"> · {qty(p.qty)}</span>}
                      </div>
                    )) : '–'}
                  </td>
                  <td className="num ci-total">{money(num(r.total) || Math.abs(num(r.amount)))}</td>
                  <td className="num">
                    <div className="row-actions">
                      <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" disabled={busy != null} onClick={() => decide(r, false)}><X size={13} />Reject</button>
                      <button type="button" className="ci-btn ci-btn-primary ci-btn-sm" disabled={busy != null} onClick={() => decide(r, true)}><Check size={13} />{busy === r.je_id ? 'Saving…' : 'Approve'}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <section className="doc-card inv-limit">
        <div className="doc-card-head">
          <h2>When approval is needed</h2>
          <span>{savedLimit > 0 ? `Over ${money(savedLimit)}` : 'Only when someone asks for it'}</span>
        </div>
        <div className="inv-limit-row">
          <label className="ci-form-field">
            <span>Adjustments over this amount wait for an admin</span>
            <input value={limit} onChange={(e) => setLimit(e.target.value)} inputMode="decimal" placeholder="Blank: no limit" />
          </label>
          <button type="button" className="ci-btn ci-btn-ghost" disabled={savingLimit} onClick={saveLimit}>{savingLimit ? 'Saving…' : 'Save'}</button>
        </div>
      </section>
    </div>
  )
}
